(() => {
  if (customElements.get('xz-v2-article')) return;
  const editorialModules = article => Array.from(article.querySelectorAll('[data-article-kind]')).filter(node =>
    !['grid-item', 'comparison-row'].includes(node.dataset.articleKind));
  const refreshEditorChecks = () => {
    if (!window.Shopify || !window.Shopify.designMode) return;
    document.querySelectorAll('xz-v2-article').forEach(article => {
      if (article.validateAuthoring) article.validateAuthoring();
    });
  };
  const initialHover = window.innerWidth > 991;
  // Comparison region names, unique per page (a repeated heading gets " (2)"); kept per element across editor re-renders.
  const comparisonNames = new WeakMap();
  const comparisonCounts = {};
  const optimizeFloat = value => window.XZV2FrameClock ? window.XZV2FrameClock.optimizeFloat(value) : (Math.abs(Math.round(value * 100000) / 100000) > 0.0001 ? Math.round(value * 100000) / 100000 : 0);
  customElements.define('xz-v2-article', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.cleanup = [];
      this.toggleAttribute('data-v2-hover', initialHover);
      const clock = window.XZV2FrameClock;
      // Hold the shared frame clock only while an arrow animates (as xz-v2-hero.js does), not for the page lifetime.
      const holds = new Map();
      const hold = key => { if (clock && !holds.has(key)) holds.set(key, clock.acquire()); };
      const release = key => { const r = holds.get(key); if (r) { r(); holds.delete(key); } };
      this.cleanup.push(() => { holds.forEach(r => r()); holds.clear(); });
      const listen = (node, event, callback, options) => {
        node.addEventListener(event, callback, options);
        this.cleanup.push(() => node.removeEventListener(event, callback, options));
      };
      // Source a-16/a-17 uses a 500ms outQuad on desktop IX2 media only.
      const arrows = new Map();
      this.querySelectorAll('.xz-v2-article-cta .icon-svg-embed').forEach(icon => {
        const state = { x: 0, frame: null }; arrows.set(icon, state);
        const move = to => {
          if (window.innerWidth <= 991) return;
          cancelAnimationFrame(state.frame);
          const from = state.x, start = clock ? clock.now() : performance.now();
          const paint = x => { state.x = x; icon.style.transform = `translate3d(${x}px, 0px, 0px)`; };
          if (matchMedia('(prefers-reduced-motion: reduce)').matches) { paint(to); release(icon); return; }
          hold(icon);
          const tick = now => {
            const p = optimizeFloat(Math.min(1, Math.max(0, (now - start) / 500)));
            paint(from + (to - from) * optimizeFloat(1 - (1 - p) ** 2));
            state.frame = p < 1 ? requestAnimationFrame(tick) : null;
            if (p >= 1) release(icon);
          };
          state.frame = requestAnimationFrame(tick);
        };
        const button = icon.closest('.xz-v2-article-cta');
        listen(button, 'mouseenter', () => move(-4));
        listen(button, 'mouseleave', () => move(0));
      });
      let desktop = window.innerWidth > 991;
      listen(window, 'resize', () => {
        const next = window.innerWidth > 991;
        if (desktop === next) return;
        desktop = next;
        arrows.forEach((state, icon) => { cancelAnimationFrame(state.frame); release(icon); state.x = 0; icon.style.removeProperty('transform'); });
      });
      this.cleanup.push(() => arrows.forEach((state, icon) => { cancelAnimationFrame(state.frame); icon.style.removeProperty('transform'); }));
      if (window.Shopify && window.Shopify.designMode) {
        refreshEditorChecks();
        listen(document, 'shopify:section:reorder', () => this.validateAuthoring());
        listen(document, 'shopify:section:load', () => this.validateAuthoring());
        listen(document, 'shopify:section:unload', () => this.validateAuthoring());
      }
      // Legacy table regions are named after their caption or the nearest heading above them, so
      // each landmark is distinguishable ("Nakiri vs santoku at a glance, table, scroll sideways…").
      const tableNames = {};
      this.querySelectorAll('.xz-v2-article-legacy .xz-lp-table').forEach(region => {
        const caption = region.querySelector('caption');
        let name = caption ? caption.textContent : '';
        for (let node = region; !name.trim() && node && !node.classList.contains('xz-v2-article-legacy'); node = node.parentElement) {
          for (let prev = node.previousElementSibling; prev && !name.trim(); prev = prev.previousElementSibling) {
            const headings = /^H[2-6]$/.test(prev.tagName) ? [prev] : prev.querySelectorAll('h2, h3, h4, h5, h6');
            if (headings.length) name = headings[headings.length - 1].textContent;
          }
        }
        name = name.replace(/\s+/g, ' ').trim().replace(/[:.]$/, '');
        if (!name) name = 'Comparison';
        tableNames[name] = (tableNames[name] || 0) + 1;
        if (tableNames[name] > 1) name += ' (' + tableNames[name] + ')';
        region.setAttribute('aria-label', name + ', table, scroll sideways for more');
        // The table keeps the plain name while its region is not scrollable (no region then; see fitRegion below).
        const table = region.querySelector('table');
        if (table && !caption && !table.hasAttribute('aria-label')) table.setAttribute('aria-label', name);
      });
      // Comparison modules: each region (and its table) is named after the nearest heading above it, across the
      // article's sections, so the landmarks are distinguishable ("Steel types, table, scroll sideways for more");
      // the corner header cell left empty by the design gets a visually hidden "Feature" (axe empty-table-header).
      const pageHeadings = Array.from(document.querySelectorAll('xz-v2-article h2, xz-v2-article h3, xz-v2-article h4'));
      this.querySelectorAll('.comparison-slider .ff-carousel-parent').forEach(scroller => {
        let name = '';
        for (const heading of pageHeadings) {
          if (scroller.contains(heading) || !(heading.compareDocumentPosition(scroller) & Node.DOCUMENT_POSITION_FOLLOWING)) break;
          if (heading.textContent.trim()) name = heading.textContent;
        }
        name = name.replace(/\s+/g, ' ').trim().replace(/[:.?]$/, '') || 'Comparison';
        const seen = comparisonNames.get(scroller);
        if (!seen) {
          const count = (comparisonCounts[name] || 0) + 1;
          comparisonCounts[name] = count;
          comparisonNames.set(scroller, count > 1 ? name + ' (' + count + ')' : name);
        }
        const label = comparisonNames.get(scroller);
        scroller.setAttribute('aria-label', label + ', table, scroll sideways for more');
        const table = scroller.querySelector('[role="table"]');
        if (table) table.setAttribute('aria-label', label);
        scroller.querySelectorAll('[role="columnheader"]').forEach(cell => {
          if (cell.textContent.trim()) return;
          const hidden = document.createElement('span');
          hidden.className = 'xz-v2-article-sr';
          hidden.textContent = 'Feature';
          hidden.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0';
          cell.appendChild(hidden);
        });
      });
      // A scroller is a focusable region named "…, scroll sideways for more" only while it really scrolls
      // (scrollWidth > clientWidth + 1, re-checked on resize); otherwise it is no Tab stop and has no region or label
      // (its table keeps the plain name). Attributes only, no pixel change; without ResizeObserver it stays a region.
      const fitRegion = (scroller, name) => {
        const sync = () => {
          const on = !window.ResizeObserver || scroller.scrollWidth > scroller.clientWidth + 1;
          if (on) {
            if (scroller.getAttribute('tabindex') !== '0') scroller.tabIndex = 0;
            if (scroller.getAttribute('role') !== 'region') scroller.setAttribute('role', 'region');
            if (scroller.getAttribute('aria-label') !== name) scroller.setAttribute('aria-label', name);
          } else {
            ['tabindex', 'role', 'aria-label'].forEach(attr => { if (scroller.hasAttribute(attr)) scroller.removeAttribute(attr); });
          }
        };
        sync();
        if (!window.ResizeObserver) return;
        const observer = new ResizeObserver(sync);
        observer.observe(scroller);
        if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
        this.cleanup.push(() => observer.disconnect());
      };
      this.querySelectorAll('.comparison-slider .ff-carousel-parent, [data-xz-drag]').forEach(scroller => {
        fitRegion(scroller, scroller.getAttribute('aria-label') || 'Comparison table, scroll sideways for more');
        let drag = null;
        let suppress = false;
        // Milen's inline carousel script: every non-touch pointer (mouse and pen) drags, the
        // scroller follows from the first pixel with the grabbing cursor, and a move of more than
        // 3px suppresses the click. Capture waits for that 3px: capturing on pointerdown (as the
        // source does) retargets the click of a plain press to the scroller, so card links would
        // never navigate. Text selection is blocked from the press by [data-dragging].
        listen(scroller, 'pointerdown', event => {
          if (event.pointerType === 'touch' || event.button !== 0) return;
          drag = { x: event.clientX, left: scroller.scrollLeft, id: event.pointerId };
          suppress = false;
          scroller.setAttribute('data-dragging', '');
        });
        listen(scroller, 'pointermove', event => {
          if (!drag || event.pointerId !== drag.id) return;
          if (!(event.buttons & 1)) { finish(); return; }
          const distance = event.clientX - drag.x;
          if (Math.abs(distance) > 3 && !suppress) {
            try { scroller.setPointerCapture(drag.id); } catch (e) {}
            suppress = true;
          }
          scroller.scrollLeft = drag.left - distance;
        });
        const finish = () => {
          if (drag && scroller.hasPointerCapture(drag.id)) scroller.releasePointerCapture(drag.id);
          drag = null;
          scroller.removeAttribute('data-dragging');
        };
        // Card links and images are natively draggable; a native drag cancels the pointer stream.
        listen(scroller, 'dragstart', event => event.preventDefault());
        listen(scroller, 'pointerup', finish);
        listen(scroller, 'pointercancel', finish);
        listen(scroller, 'lostpointercapture', finish);
        listen(scroller, 'click', event => {
          if (suppress) { event.preventDefault(); event.stopPropagation(); suppress = false; }
        }, true);
        this.cleanup.push(finish);
      });
      // Legacy bodies are rewritten by the inline snippets/xz-v2-article-legacy-transform.liquid as
      // soon as they are parsed. After a theme-editor re-render the inline script may not have run:
      // re-execute it (it only defines the function when not next to a body), then apply it.
      const legacy = this.querySelectorAll('.xz-v2-article-legacy:not([data-lp-done])');
      if (legacy.length && !window.XZV2ArticleLegacy) {
        const source = this.querySelector('script[data-xz-legacy-transform]');
        if (source) { const run = document.createElement('script'); run.textContent = source.textContent; document.head.appendChild(run).remove(); }
      }
      if (window.XZV2ArticleLegacy) legacy.forEach(body => window.XZV2ArticleLegacy(body));
      // Comparison rows share the source's grid tracks through display:contents.
      // Scroll their first rendered cell; ordinary modules have real editor boxes.
      listen(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const rendered = Array.from(event.target.querySelectorAll('*')).find(node => node.getBoundingClientRect().height > 0);
        if (rendered) rendered.scrollIntoView({ block: 'center', behavior: 'auto' });
      });
    }
    validateAuthoring() {
      if (this.editorNote) this.editorNote.remove();
      this.editorNote = null;
      const warnings = [];
      const modules = editorialModules(this);
      // Milen's published article has no leading-image caption, so it is not required here.
      let previous = null;
      if (this.hasAttribute('data-continuation') && !this.querySelector('.xz-v2-article-legacy')) {
        const sections = Array.from(document.querySelectorAll('xz-v2-article'));
        for (let index = sections.indexOf(this) - 1; index >= 0; index--) {
          const section = sections[index];
          if (section.querySelector('.xz-v2-article-legacy')) break;
          const earlier = editorialModules(section);
          if (earlier.length) { previous = earlier[earlier.length - 1]; break; }
          if (!section.hasAttribute('data-continuation')) break;
        }
      }
      // Consecutive icon-listing and bullet blocks are items in one source-authored list.
      const special = ['priority-one', 'priority-two', 'list-grid', 'comparison', 'image'];
      modules.forEach((node, index) => {
        const kind = node.dataset.articleKind;
        if (node.textContent.includes('—')) warnings.push('Replace the em dash in module ' + (index + 1) + '.');
        if (node.querySelector('em,i')) warnings.push('Use upright text in module ' + (index + 1) + '.');
        if (kind === 'heading' && !node.textContent.trim()) warnings.push('Add the H2 heading in module ' + (index + 1) + '.');
        if (kind === 'image') {
          const caption = node.querySelector('.xz-is-article-image-caption');
          if (!caption || !caption.textContent.trim()) warnings.push('Add the caption for image module ' + (index + 1) + '.');
        }
        if (previous && special.includes(kind) && previous.dataset.articleKind === kind)
          warnings.push('Separate adjacent ' + kind.replace(/-/g, ' ') + ' modules.');
        previous = node;
      });
      if (!warnings.length) return;
      const note = document.createElement('details');
      note.className = 'xz-v2-article-editor-note';
      const summary = document.createElement('summary');
      summary.textContent = 'Article authoring: ' + warnings.length + ' checks need attention';
      note.appendChild(summary);
      const list = document.createElement('ul');
      warnings.forEach(text => { const item = document.createElement('li'); item.textContent = text; list.appendChild(item); });
      note.appendChild(list);
      this.prepend(note);
      this.editorNote = note;
    }
    disconnectedCallback() {
      this.ready = false;
      (this.cleanup || []).forEach(fn => fn());
      this.cleanup = [];
      if (this.editorNote) this.editorNote.remove();
      this.editorNote = null;
      refreshEditorChecks();
    }
  });
})();
